import { useState, useEffect, useRef } from 'react';
import { Song } from '../types';

export const useAudio = (initialVolume = 0.5) => {
  const [currentSong, setCurrentSong] = useState<Song | null>(null);
  const [nextSong, setNextSong] = useState<Song | null>(null);
  const [isPaused, setIsPaused] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const [volume, setVolume] = useState(initialVolume);

  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const nextAudioRef = useRef<HTMLAudioElement | null>(null);
  const tempAudioRef = useRef<HTMLAudioElement | null>(null);

  // Clean up temporary audio on unmount
  useEffect(() => {
    return () => {
      if (tempAudioRef.current) {
        tempAudioRef.current.pause();
        tempAudioRef.current = null;
      }
    };
  }, []);

  // Apply volume changes to all audio elements
  useEffect(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.volume = volume;
    }
    if (tempAudioRef.current) {
      tempAudioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlayPause = () => {
    if (!currentAudioRef.current || !currentSong) return;
    
    if (isPaused) {
      currentAudioRef.current.play()
        .then(() => {
          setIsPaused(false);
        })
        .catch(console.error);
    } else {
      currentAudioRef.current.pause();
      setIsPaused(true);
    }
  };

  const fadeToNextSong = () => {
    if (!nextSong || !currentSong || !nextAudioRef.current || !currentAudioRef.current || isFading) {
      console.log("Cannot fade: missing refs or already fading");
      return;
    }
    
    setIsFading(true);
    console.log("Starting fade to next song:", nextSong.title);

    try {
      // Make sure we have the current volume set correctly
      if (currentAudioRef.current) {
        currentAudioRef.current.volume = volume;
      }
      
      // Clean up any existing temp audio
      if (tempAudioRef.current) {
        tempAudioRef.current.pause();
        tempAudioRef.current = null;
      }
      
      // Create a temporary audio element to avoid issues with state transitions
      const tempNext = new Audio(nextSong.downloadUrl);
      tempNext.volume = 0;
      tempAudioRef.current = tempNext;
      
      tempNext.addEventListener('canplaythrough', () => {
        // Start playing the temporary audio
        tempNext.play()
          .then(() => {
            console.log("Temp audio playing, starting fade");
            
            // Fade out current song, fade in next song
            const fadeDuration = 2000; // 2 seconds fade
            const fadeInterval = 50; // 50ms update interval
            const steps = fadeDuration / fadeInterval;
            const volumeStep = volume / steps;
            
            let currentStep = 0;
            
            const fadeTimer = setInterval(() => {
              currentStep++;
              
              // Decrease volume of current song
              if (currentAudioRef.current) {
                currentAudioRef.current.volume = Math.max(0, volume - (volumeStep * currentStep));
              }
              
              // Increase volume of temp next song
              tempNext.volume = Math.min(volume, volumeStep * currentStep);
              
              // When fade is complete
              if (currentStep >= steps) {
                clearInterval(fadeTimer);
                
                if (currentAudioRef.current) {
                  currentAudioRef.current.pause();
                  currentAudioRef.current.currentTime = 0;
                }
                
                const newCurrentSongAfterFade = nextSong; 
                setCurrentSong(newCurrentSongAfterFade);
                if (newCurrentSongAfterFade) {
                  setNextSong(null); 
                }
                
                setTimeout(() => {
                  if (currentAudioRef.current && tempAudioRef.current && newCurrentSongAfterFade) {
                    // Record the current time of the temp audio
                    const currentTime = tempAudioRef.current.currentTime;
                    
                    // Configure main audio player
                    currentAudioRef.current.volume = volume;
                    currentAudioRef.current.currentTime = currentTime;
                    
                    // Play the main audio player
                    currentAudioRef.current.play()
                      .then(() => {
                        console.log("Successfully transferred playback to main audio element");
                        // Stop the temp audio
                        tempAudioRef.current?.pause();
                        tempAudioRef.current = null;
                      })
                      .catch(e => {
                        console.error("Failed to transfer playback to main audio:", e);
                        // Keep temp audio playing as fallback
                      });
                  }
                  
                  // Reset fading state
                  setIsFading(false);
                }, 100); // Short delay to ensure state updates have applied
              }
            }, fadeInterval);
          })
          .catch(error => {
            console.error("Failed to play temp audio:", error);
            handleFadeError();
          });
      });
      
      tempNext.addEventListener('error', () => {
        console.error("Temp audio error");
        handleFadeError();
      });
      
      // Start loading the audio
      tempNext.load();
      
    } catch (error) {
      console.error("Error in fade process:", error);
      handleFadeError();
    }
  };

  const handleFadeError = () => {
    // Reset fading state and try to play the next song directly
    setIsFading(false);
    
    // Clean up any existing temp audio
    if (tempAudioRef.current) {
      tempAudioRef.current.pause();
      tempAudioRef.current = null;
    }
    
    if (nextSong) {
      // Move to the next song
      const songToPlayAfterError = nextSong;
      setCurrentSong(songToPlayAfterError);
      setNextSong(null); 
      
      // Try to play the new current song after a short delay
      setTimeout(() => {
        if (currentAudioRef.current && songToPlayAfterError) {
          currentAudioRef.current.volume = volume;
          currentAudioRef.current.play()
            .catch(e => console.error("Failed to play next song after fade error:", e));
        }
      }, 300);
    }
  };

  const handleAudioEnded = () => {
    if (isFading) return;

    // Add check to ensure the song actually played a significant amount
    if (currentAudioRef.current && currentAudioRef.current.currentTime < 2) {
      console.log("Song ended too quickly, likely an error. Trying next song directly");
      // Song ended too quickly, likely an error
      if (nextSong) {
        const newCurrentSong = nextSong;
        setCurrentSong(newCurrentSong);
        setNextSong(null); 
        
        setTimeout(() => {
          if (currentAudioRef.current && newCurrentSong) {
            currentAudioRef.current.volume = volume;
            currentAudioRef.current.play()
              .catch(e => console.error("Failed to play next song after quick ending:", e));
          }
        }, 300);
      }
      return;
    }

    // Start fading between songs if we have a next song
    if (nextSong) {
      fadeToNextSong();
    }
  };

  const startMusic = () => {
    if (currentAudioRef.current && currentSong && isPaused) {
      currentAudioRef.current.play()
        .then(() => {
          setIsPaused(false);
        })
        .catch(console.error);
    }
  };

  return {
    currentSong,
    setCurrentSong,
    nextSong,
    setNextSong,
    isPaused,
    setIsPaused,
    volume,
    setVolume,
    isFading,
    currentAudioRef,
    nextAudioRef,
    togglePlayPause,
    handleAudioEnded,
    startMusic
  };
}; 